import React, { useEffect, useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { ArrowLeft, Search, MapPin, ArrowUpRight, Store, Coffee, Fuel, Dumbbell, Wine, PawPrint, Scissors, Baby, Shirt, Car, Wrench, Home, PartyPopper } from "lucide-react";
import { api, assetUrl } from "../lib/api";

const ICONS = {
  Cafe: Coffee, "Fuel & Convenience": Fuel, Fitness: Dumbbell, Retail: Store,
  "Beauty & Wellness": Scissors, Childcare: Baby, Sports: Dumbbell, "Events & Services": PartyPopper,
  Automotive: Car, "Trades & Services": Wrench, "Home Builder": Home,
};

const CommunityBusinesses = () => {
  const [items, setItems] = useState([]);
  const [cats, setCats] = useState([]);
  const [q, setQ] = useState("");
  const [cat, setCat] = useState("all");

  useEffect(() => {
    api.get("/community/businesses").then((r) => { setItems(r.data.items); setCats(r.data.categories); });
  }, []);

  const filtered = useMemo(() => items.filter((b) =>
    (cat === "all" || b.category === cat) &&
    (!q || b.name.toLowerCase().includes(q.toLowerCase()))
  ), [items, q, cat]);

  return (
    <div className="bg-[#12201A] min-h-screen pt-[92px]" data-testid="local-businesses-page">
      <div className="max-w-[1200px] mx-auto px-5 md:px-8 py-10">
        <Link to="/community" className="inline-flex items-center gap-2 text-[#C9B79C] text-sm mb-6"><ArrowLeft size={15} /> Community</Link>
        <p className="eyebrow text-[#C9B79C] mb-2">Community · Local Businesses</p>
        <h1 className="font-display font-light text-4xl md:text-5xl text-[#F3EEE3]">Shops, services &amp; local specialists</h1>
        <p className="text-[#F3EEE3]/65 mt-3 max-w-2xl leading-relaxed">Businesses located in — or serving — the Deer Park community, from the plaza at the entrance to independent home-based specialists.</p>

        {/* controls */}
        <div className="mt-8 flex flex-col sm:flex-row gap-3">
          <div className="relative flex-1 max-w-md">
            <Search size={16} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#F3EEE3]/40" />
            <input
              data-testid="business-search"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              placeholder="Search by name…"
              className="w-full bg-[#0F1B15] border border-[#F3EEE3]/15 rounded-full pl-10 pr-4 py-2.5 text-sm text-[#F3EEE3] placeholder:text-[#F3EEE3]/35 focus:outline-none focus:border-[#C9B79C]/50"
            />
          </div>
          <div className="flex gap-2 overflow-x-auto hide-scrollbar pb-1">
            {["all", ...cats].map((c) => (
              <button
                key={c}
                data-testid={`cat-${c}`}
                onClick={() => setCat(c)}
                className={`shrink-0 text-xs px-3.5 py-2 rounded-full border transition-colors ${cat === c ? "bg-[#C9B79C] text-[#12201A] border-[#C9B79C]" : "border-[#F3EEE3]/15 text-[#F3EEE3]/70 hover:border-[#C9B79C]/40"}`}
              >
                {c === "all" ? "All" : c}
              </button>
            ))}
          </div>
        </div>

        {/* grid */}
        <div className="grid sm:grid-cols-2 lg:grid-cols-3 gap-4 mt-8" data-testid="business-grid">
          {filtered.map((b) => {
            const Icon = ICONS[b.category] || Store;
            const inner = (
              <>
                {b.image ? (
                  <div className="aspect-[16/10] overflow-hidden bg-white"><img src={assetUrl(b.image)} alt={b.name} className="w-full h-full object-cover" /></div>
                ) : (
                  <div className="aspect-[16/10] bg-[#12201A] flex items-center justify-center text-[#C9B79C]/70"><Icon size={34} /></div>
                )}
                <div className="p-5">
                  <div className="flex items-center justify-between gap-2">
                    <p className="text-[#C9B79C]/90 text-xs">{b.category}</p>
                    <span className={`text-[0.6rem] uppercase tracking-wide px-2 py-0.5 rounded-full ${b.area_tag === "located" ? "bg-[#3E7A5E]/25 text-[#8fd0b0]" : "bg-[#5B6B7A]/25 text-[#a9bccb]"}`}>
                      {b.area_tag === "located" ? "In Deer Park" : "Serving area"}
                    </span>
                  </div>
                  <p className="text-[#F3EEE3] font-medium mt-1.5 flex items-center gap-1.5">{b.name}{(b.website || b.maps_url) && <ArrowUpRight size={13} className="text-[#F3EEE3]/30" />}</p>
                  {b.description && <p className="text-[#F3EEE3]/55 text-sm mt-1.5 leading-relaxed line-clamp-2">{b.description}</p>}
                  {b.address && <p className="text-[#F3EEE3]/40 text-xs mt-3 flex items-center gap-1"><MapPin size={11} /> {b.address}</p>}
                </div>
              </>
            );
            const href = b.website || b.maps_url;
            return (
              <a key={b.id} href={href} target="_blank" rel="noreferrer" data-testid={`business-${b.id}`} className="group rounded-2xl overflow-hidden border border-[#F3EEE3]/12 bg-[#0F1B15] hover:border-[#C9B79C]/40 transition-colors">
                {inner}
              </a>
            );
          })}
        </div>
        {filtered.length === 0 && <p className="text-[#F3EEE3]/50 text-sm mt-10 text-center">No businesses match your search.</p>}
      </div>
    </div>
  );
};

export default CommunityBusinesses;
